/**
 * Pixel propio de la app (1-10-2026).
 *
 * Por qué: la app ya carga GA4 (`public/index.html`), pero GA4 no sabe QUIÉN es cada visita (cuenta,
 * plan, créditos), no deja unir lo que alguien hizo antes de registrarse con lo que hace después y
 * agrega demasiado para preguntas como «¿cuántas cuentas con 5 créditos miraron los precios y no
 * pulsaron ningún plan?». Esto manda eventos a nuestra propia tabla (`app_events`, Lambda
 * `app_events`) con la cuenta, el plan y los créditos del momento.
 *
 *   track("pricing_view", { from: "/tokens" })     evento explícito
 *   trackPageView(pathname)                          lo llama App.js en cada cambio de ruta
 *   setTrackContext({ plan, credits, lang })         lo llama App.js cuando cambia el usuario
 *
 * Reglas: nunca lanza ni rompe la app; sin REACT_APP_BACKEND_URL (modo mock) o en tests no hace nada;
 * propiedades solo escalares cortos y NUNCA valores de documentos ni correos. `aid` (id anónimo del
 * navegador, localStorage) viaja en todos los eventos: une el antes y el después del registro.
 * Los eventos comerciales se reflejan también en GA4 para verlos con la atribución de origen.
 */

const ENDPOINT = process.env.REACT_APP_BACKEND_URL ? `${process.env.REACT_APP_BACKEND_URL}/events` : null;
const ENABLED = Boolean(ENDPOINT) && process.env.NODE_ENV !== "test";
const FLUSH_MS = 2500;
const MAX_PER_REQUEST = 25;
const GA_EVENTS = new Set(["pricing_view", "plan_select", "checkout_start", "pack_click", "checkout_return", "upload_success"]);

let queue = [];
let timer = null;
let context = {};
let lastView = null; // { path, at }

// Campaña de origen (correos del recorrido: ?utm_campaign=activation|almost_out|winback). Se guarda la primera de la
// sesión del navegador y viaja como `utm` en los eventos, para ver qué correos traen de vuelta y cuáles acaban en pago.
const utmCampaign = () => {
  try {
    const q = new URLSearchParams(window.location.search).get("utm_campaign");
    if (q && /^[a-z0-9_-]{1,40}$/i.test(q) && !sessionStorage.getItem("ws_utm")) sessionStorage.setItem("ws_utm", q);
    return sessionStorage.getItem("ws_utm") || undefined;
  } catch {
    return undefined;
  }
};

const uuid = () =>
  (typeof crypto !== "undefined" && crypto.randomUUID && crypto.randomUUID()) ||
  `${Date.now().toString(16)}-${Math.random().toString(16).slice(2, 10)}`;

function stored(storage, key) {
  try {
    let v = storage.getItem(key);
    if (!v) {
      v = uuid();
      storage.setItem(key, v);
    }
    return v;
  } catch {
    return null; // modo privado estricto: sin id, el evento sigue (sin unir sesiones)
  }
}

const optedOut = () => {
  try {
    return localStorage.getItem("ws_no_track") === "1" || navigator.doNotTrack === "1";
  } catch {
    return false;
  }
};

// Rutas con ids (resultados, escáneres, tickets) → una sola ruta lógica.
export const normalizePath = (p) =>
  String(p || "/")
    .replace(/[0-9a-f]{8}-[0-9a-f-]{27,}/gi, ":id")
    .replace(/\/\d{3,}/g, "/:id")
    .slice(0, 160);

export function setTrackContext(next) {
  context = { ...context, ...next };
}

function flush({ beacon = false } = {}) {
  clearTimeout(timer);
  timer = null;
  if (!ENABLED || !queue.length) return;
  const batch = queue.splice(0, MAX_PER_REQUEST);
  const body = JSON.stringify({
    aid: stored(localStorage, "ws_aid"),
    sid: stored(sessionStorage, "ws_sid"),
    internal: /^(localhost|127\.0\.0\.1)$/.test(window.location.hostname) || undefined,
    events: batch,
  });
  const headers = { "Content-Type": "application/json" };
  try {
    const token = localStorage.getItem("token");
    if (token) headers.Authorization = `Bearer ${token}`;
  } catch {
    // sin token: el evento queda como anónimo
  }
  try {
    fetch(ENDPOINT, { method: "POST", headers, body, keepalive: beacon || body.length < 60000 }).catch(() => {});
  } catch {
    // nunca romper la app por una métrica
  }
  if (queue.length) timer = setTimeout(flush, 200);
}

export function track(name, props = {}) {
  if (!ENABLED || optedOut()) return;
  try {
    const clean = {};
    for (const [k, v] of Object.entries({ plan: context.plan, credits: context.credits, lang: context.lang, utm: utmCampaign(), ...props })) {
      if (v === undefined || v === null) continue;
      clean[k] = typeof v === "string" ? v.slice(0, 120) : v;
    }
    queue.push({ e: name, ts: Date.now(), path: normalizePath(window.location.pathname), props: clean });
    if (GA_EVENTS.has(name) && window.gtag) window.gtag("event", name, clean);
    if (queue.length >= MAX_PER_REQUEST) flush();
    else if (!timer) timer = setTimeout(flush, FLUSH_MS);
  } catch {
    // idem
  }
}

/** Vista de página con la anterior y cuánto se estuvo en ella (para ver por dónde se navega). */
export function trackPageView(pathname) {
  const path = normalizePath(pathname);
  const now = Date.now();
  const prev = lastView && lastView.path !== path ? lastView : null;
  if (lastView && lastView.path === path) return; // mismo path (cambio de query): no es otra vista
  track("page_view", prev ? { prev: prev.path, dwell_s: Math.min(3600, Math.round((now - prev.at) / 1000)) } : {});
  lastView = { path, at: now };
}

if (ENABLED && typeof window !== "undefined") {
  const leave = () => {
    if (lastView && !lastView.left) {
      lastView.left = true;
      track("page_leave", { dwell_s: Math.min(3600, Math.round((Date.now() - lastView.at) / 1000)) });
    }
    flush({ beacon: true });
  };
  window.addEventListener("pagehide", leave);
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "hidden") leave();
    else if (lastView) {
      lastView.left = false;
      lastView.at = Date.now(); // el tiempo en segundo plano no cuenta como lectura
    }
  });
}
